import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, Hide, Text, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useEffect, useState} from 'react';

import {head} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {FileResponseBody} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {
  useGetSaleVehicleWarehouseInformationQuery,
  useLazyGetVehicleWarehouseMovementQuery,
  useReReceiveVehicleToVehicleWarehouseMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {getApiDateString, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface VehicleWarehouseStockInStockOutProps extends TestIdProps {
  vehicleId: string;
  onDocumentsCreation: (documents: FileResponseBody[]) => void;
  onMovementComplete?: () => void;
  isNotDismissible?: boolean;
}

interface VehicleWarehouseRestockInFormFields {
  vehicleWarehouseId: string;
  movementAt: string;
  note: string | null;
}

export function VehicleWarehouseRestockIn(props: VehicleWarehouseStockInStockOutProps) {
  const [isStockInDocumentSelected, setIsStockInDocumentSelected] = useState(true);
  const [isCancelStockInDocumentSelected, setIsCancelStockInDocumentSelected] = useState(true);
  const [stockInDocumentTemplate, setStockInDocumentTemplate] = useState<TemplateBoxTemplate>();
  const [cancelStockInDocumentTemplate, setCancelStockInDocumentTemplate] =
    useState<TemplateBoxTemplate>();

  const {
    data: saleVehicle,
    isLoading: isSaleVehicleLoading,
    isError: hasSaleVehicleError,
  } = useGetSaleVehicleQuery({
    vehicleId: props.vehicleId,
  });

  const {
    data: warehouseInfo,
    isLoading: isWarehouseInfoLoading,
    isError: hasWarehouseInfoError,
  } = useGetSaleVehicleWarehouseInformationQuery(
    {
      vehicleId: props.vehicleId,
      saleVehicleId: saleVehicle?.id!,
    },
    {
      skip: isNilOrEmpty(saleVehicle),
    }
  );

  const currentWarehouse = saleVehicle?.vehicleWarehouse;

  const [getTemplates, {isLoading: isTemplatesV2Loading, isError: isTemplatesV2Error}] =
    useDocumentTemplates();

  const {
    defaultTemplates: stockInDefaultTemplates,
    selectedTemplates: stockInSelectedTemplates,
    selectedAuthProfileLabel: stockInSelectedAuthProfileLabel,
    primaryTemplate: stockInPrimaryTemplate,
  } = getTemplates('receive-vehicle-warehouse-movement');
  const {
    defaultTemplates: cancelStockInDefaultTemplates,
    selectedTemplates: cancelStockInSelectedTemplates,
    selectedAuthProfileLabel: cancelStockInSelectedAuthProfileLabel,
    primaryTemplate: cancelStockInPrimaryTemplate,
  } = getTemplates('cancel-receive-vehicle-warehouse-movement');

  const [restockVehicle] = useReReceiveVehicleToVehicleWarehouseMutation();
  const [getMovement] = useLazyGetVehicleWarehouseMovementQuery();

  useEffect(() => {
    if (isNotNilOrEmpty(stockInDocumentTemplate)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStockInDocumentTemplate(stockInPrimaryTemplate);
  }, [stockInPrimaryTemplate, stockInDocumentTemplate]);

  useEffect(() => {
    if (isNotNilOrEmpty(cancelStockInDocumentTemplate)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCancelStockInDocumentTemplate(cancelStockInPrimaryTemplate);
  }, [cancelStockInDocumentTemplate, cancelStockInPrimaryTemplate]);

  const warehouseOptions = [
    {
      label: currentWarehouse?.name,
      value: currentWarehouse?.id,
    },
  ];

  const onSubmit: FormSubmitHandler<VehicleWarehouseRestockInFormFields> = (values) =>
    restockVehicle({
      vehicleId: props.vehicleId,
      reReceiveVehicleToVehicleWarehouseRequestBody: {
        vehicleWarehouseId: values.vehicleWarehouseId,
        receiveDate: values.movementAt,
        note: values.note,
        receiptCancelTemplateId: cancelStockInDocumentTemplate
          ? cancelStockInDocumentTemplate.id
          : undefined,
        receiptTemplateId: stockInDocumentTemplate ? stockInDocumentTemplate.id : undefined,
      },
    })
      .unwrap()
      .then((response) =>
        Promise.all([
          getMovement({
            vehicleWarehouseMovementId: response?.receiptMovementId,
          }).unwrap(),
          getMovement({
            vehicleWarehouseMovementId: response?.receiptCancelMovementId,
          }).unwrap(),
        ])
      )
      .then(([stockInReceiptResponse, cancelReceiptResponse]) => {
        stockInReceiptResponse.document &&
          cancelReceiptResponse.document &&
          props.onDocumentsCreation([
            cancelReceiptResponse.document,
            stockInReceiptResponse.document,
          ]);
      })
      .then(props.onMovementComplete)
      .catch(handleApiError);

  const defaultValues: Partial<VehicleWarehouseRestockInFormFields> = {
    vehicleWarehouseId: head(warehouseOptions)?.value,
    movementAt: warehouseInfo?.receivedAt ?? getApiDateString(new Date()),
  };

  const isLoading = isSaleVehicleLoading || isWarehouseInfoLoading || isTemplatesV2Loading;
  const isError = hasSaleVehicleError || hasWarehouseInfoError || isTemplatesV2Error;

  return (
    <VStack spacing={4}>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNilOrEmpty(currentWarehouse)}
        emptyMessage={i18n.t('entity.vehicleWarehouse.labels.noWarehouseForBranch')}
      >
        <Form<VehicleWarehouseRestockInFormFields>
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          schema={schema}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <Text>{i18n.t('entity.vehicleWarehouse.modal.restockAlert')}</Text>
              <Grid columns={2}>
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.warehouse')}
                  isDisabled
                  name="vehicleWarehouseId"
                  type="choice"
                  options={warehouseOptions}
                  control={control}
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId('vehicleWarehouseId-select', props)}
                />
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.dateOfStockIn')}
                  name="movementAt"
                  type="apiDate"
                  control={control}
                  isRequired
                  data-testid={suffixTestId('movementAt', props)}
                />
              </Grid>
              <DocumentTemplateBox
                data-testid={suffixTestId('cancelStockIn-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseCancelStockInDocument')}
                templates={cancelStockInDefaultTemplates}
                authorizationTemplates={cancelStockInSelectedTemplates}
                authorizationProfileLabel={cancelStockInSelectedAuthProfileLabel}
                selectedTemplate={cancelStockInDocumentTemplate}
                isSelected={isCancelStockInDocumentSelected}
                onSelect={setIsCancelStockInDocumentSelected}
                onTemplateChange={setCancelStockInDocumentTemplate}
                isDisabled
              />
              <DocumentTemplateBox
                data-testid={suffixTestId('stockIn-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockInDocument')}
                templates={stockInDefaultTemplates}
                authorizationTemplates={stockInSelectedTemplates}
                authorizationProfileLabel={stockInSelectedAuthProfileLabel}
                selectedTemplate={stockInDocumentTemplate}
                isSelected={isStockInDocumentSelected}
                onSelect={setIsStockInDocumentSelected}
                onTemplateChange={setStockInDocumentTemplate}
                isWithNote
                isDisabled
                onNoteChange={(value) => formApi.setValue('note', value)}
                noteLabel={i18n.t('entity.document.labels.vehicleWarehouseStockInNote')}
              />
              <ButtonGroup align="right">
                <Hide when={props.isNotDismissible}>
                  <Button
                    title={i18n.t('general.actions.cancel')}
                    variant="secondary"
                    onClick={closeCurrentDialog}
                    data-testid={suffixTestId('cancel-button', props)}
                  />
                </Hide>
                <FormButton
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  control={control}
                  data-testid={suffixTestId('submit-button', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}

const schema = object({
  vehicleWarehouseId: yupString.required(),
  movementAt: yupString.required(),
  note: yupString,
});
